'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import { SermonCard, SermonCardSkeleton } from './SermonCard';
import { Button } from './ui/button';
import { ScrollReveal } from './ScrollReveal';

interface Sermon {
  guid: string;
  title: string;
  date: string;
  link: string;
  thumbnail: string | null;
}

interface SermonsResponse {
  sermons: Sermon[];
  pagination: {
    page: number;
    hasMore: boolean;
  };
  stale?: boolean;
}

export function SermonsCarousel() {
  const [sermons, setSermons] = useState<Sermon[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [staleWarning, setStaleWarning] = useState(false);

  const loadedPagesRef = useRef<Set<number>>(new Set());
  const isMountedRef = useRef(true);
  const isInitialLoadDoneRef = useRef(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const fetchPage = useCallback(async (page: number) => {
    if (loadedPagesRef.current.has(page)) {
      return;
    }

    loadedPagesRef.current.add(page);

    const isFirstPage = page === 1;

    if (isFirstPage) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const response = await fetch(`/api/sermons?page=${page}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch sermons (${response.status})`);
      }

      const data: SermonsResponse = await response.json();

      if (!isMountedRef.current) return;

      if (data.stale) {
        setStaleWarning(true);
      }

      setSermons((prev) => {
        const existingGuids = new Set(prev.map(s => s.guid));
        const newSermons = data.sermons.filter(s => !existingGuids.has(s.guid));
        return [...prev, ...newSermons];
      });

      setHasMore(data.pagination.hasMore);

      if (page === 1 && data.pagination.hasMore && isMountedRef.current) {
        setTimeout(() => {
          if (isMountedRef.current) {
            fetchPage(2);
          }
        }, 100);
      }
    } catch (err: any) {
      if (!isMountedRef.current) return;

      console.error(`Error fetching page ${page}:`, err);
      setError(`Failed to load sermons: ${err.message || 'Unknown error'}`);

      loadedPagesRef.current.delete(page);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) return;

    let nextPage = 1;
    while (loadedPagesRef.current.has(nextPage)) {
      nextPage++;
    }
    fetchPage(nextPage);
  }, [hasMore, loadingMore, fetchPage]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!isInitialLoadDoneRef.current) {
      isInitialLoadDoneRef.current = true;
      fetchPage(1);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !loadingMore && isMountedRef.current) {
          loadMore();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '200px',
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadingMore, loadMore]);

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -400, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 400, behavior: 'smooth' });
  };

  if (loading && sermons.length === 0) {
    return (
      <section className="pt-12 lg:pt-16 pb-24 lg:pb-32 bg-background relative overflow-hidden" id="sermons">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-4 mb-12">
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-accent" />
              <span className="text-sm font-medium tracking-wider uppercase text-accent">
                Latest Messages
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Recent Sermons</h2>
          </div>
          <div className="flex gap-6 overflow-x-auto pb-4">
            {[...Array(3)].map((_, i) => (
              <SermonCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error && sermons.length === 0) {
    return (
      <section className="pt-12 lg:pt-16 pb-24 lg:pb-32 bg-background relative overflow-hidden" id="sermons">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-4 mb-12">
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-accent" />
              <span className="text-sm font-medium tracking-wider uppercase text-accent">
                Latest Messages
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Recent Sermons</h2>
          </div>
          <div className="border border-destructive/30 bg-destructive/5 p-8 space-y-4">
            <p className="text-destructive font-medium">{error}</p>
            <Button
              variant="outline"
              onClick={() => {
                loadedPagesRef.current.clear();
                isInitialLoadDoneRef.current = false;
                fetchPage(1);
              }}
              className="border-destructive/30 hover:bg-destructive/10"
            >
              Try Again
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-12 lg:pt-16 pb-24 lg:pb-32 bg-background relative overflow-hidden" id="sermons">
      {/* Decorative elements */}
      <div className="hidden lg:block absolute top-20 right-16 w-6 h-6 border border-accent/20 rotate-45" />
      <div className="hidden lg:block absolute bottom-20 left-16 w-4 h-4 bg-accent/10 rotate-45" />
      <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-muted/30 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <ScrollReveal>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="w-8 h-px bg-accent" />
                <span className="text-sm font-medium tracking-wider uppercase text-accent">
                  Latest Messages
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Recent Sermons</h2>
              <p className="text-muted-foreground max-w-md">
                Explore Andrew&apos;s latest teachings and messages!
              </p>
            </div>
          </ScrollReveal>

          {/* Navigation arrows */}
          <ScrollReveal delay={200}>
            <div className="flex gap-3">
              <button
                onClick={scrollLeft}
                className="group w-12 h-12 border border-border hover:border-accent/50 hover:bg-accent/5 transition-all duration-300 flex items-center justify-center"
                aria-label="Scroll left"
              >
                <svg
                  className="w-5 h-5 text-muted-foreground group-hover:text-accent transition-colors"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={scrollRight}
                className="group w-12 h-12 border border-border hover:border-accent/50 hover:bg-accent/5 transition-all duration-300 flex items-center justify-center"
                aria-label="Scroll right"
              >
                <svg
                  className="w-5 h-5 text-muted-foreground group-hover:text-accent transition-colors"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </ScrollReveal>
        </div>

        {staleWarning && (
          <div className="mb-6 bg-accent/10 border border-accent/20 p-4 flex items-center gap-3">
            <svg className="w-5 h-5 text-accent flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm text-foreground">
              Content may be outdated. Sermons are cached and refresh hourly.
            </p>
          </div>
        )}

        {sermons.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border">
            <p className="text-muted-foreground text-lg">No sermons available at this time.</p>
          </div>
        ) : (
          <>
            <ScrollReveal delay={100}>
              <div
                ref={scrollContainerRef}
                className="flex gap-6 overflow-x-auto pb-6 scrollbar-hide -mx-6 px-6"
              >
                {sermons.map((sermon) => (
                  <SermonCard key={sermon.guid} {...sermon} />
                ))}

                {loadingMore && (
                  <>
                    <SermonCardSkeleton />
                    <SermonCardSkeleton />
                  </>
                )}

                {hasMore && !loadingMore && (
                  <div
                    ref={sentinelRef}
                    className="w-1 flex-shrink-0"
                    aria-hidden="true"
                  />
                )}
              </div>
            </ScrollReveal>

            {error && sermons.length > 0 && (
              <div className="mt-6 flex items-center gap-4 p-4 bg-destructive/5 border border-destructive/20">
                <p className="text-sm text-destructive">{error}</p>
                <Button variant="outline" size="sm" onClick={loadMore} className="border-destructive/30">
                  Retry
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
